import { appStorage } from '../lib/safeStorage';
import React, { createContext, useContext, useCallback, useRef, useEffect, useState } from 'react';

const AutoSaveContext = createContext(null);

// Storage key prefix
const STORAGE_PREFIX = 'staffed_draft_';

// Drafts are a local convenience, not a record. A draft older than this is
// treated as stale: it is discarded (removed from storage) the moment it is
// accessed and is never restored into a form.
const MAX_DRAFT_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// Read a draft only if it exists AND is not stale. A stale draft is deleted
// from storage here so it can never silently reappear.
const readValidDraft = (storageKey) => {
  let saved;
  try {
    saved = appStorage.getItem(storageKey);
  } catch (e) {
    return null;
  }
  if (!saved) return null;
  try {
    const parsed = JSON.parse(saved);
    if (parsed && parsed.savedAt) {
      const age = Date.now() - new Date(parsed.savedAt).getTime();
      if (Number.isNaN(age) || age > MAX_DRAFT_AGE_MS) {
        appStorage.removeItem(storageKey);
        return null;
      }
    }
    return parsed;
  } catch (e) {
    return null;
  }
};

/**
 * AutoSaveProvider - Provides global auto-save functionality
 * Wrap your app with this provider to enable auto-save on all forms
 */
export const AutoSaveProvider = ({ children }) => {
  const saveTimeouts = useRef({});
  const [savedForms, setSavedForms] = useState({});

  // Initialize saved forms from appStorage on mount
  useEffect(() => {
    const forms = {};
    for (let i = 0; i < appStorage.length; i++) {
      const key = appStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) {
        const formKey = key.replace(STORAGE_PREFIX, '');
        forms[formKey] = true;
      }
    }
    setSavedForms(forms);
  }, []);

  // Save form data to appStorage
  const saveForm = useCallback((formKey, data) => {
    // Clear any pending save for this form
    if (saveTimeouts.current[formKey]) {
      clearTimeout(saveTimeouts.current[formKey]);
    }

    // Debounce save by 300ms
    saveTimeouts.current[formKey] = setTimeout(() => {
      try {
        const storageKey = `${STORAGE_PREFIX}${formKey}`;
        const saveData = {
          data,
          savedAt: new Date().toISOString()
        };
        appStorage.setItem(storageKey, JSON.stringify(saveData));
        setSavedForms(prev => ({ ...prev, [formKey]: true }));
        console.debug(`AutoSave: Saved ${formKey}`);
      } catch (e) {
        console.warn('AutoSave: Failed to save', e);
      }
    }, 300);
  }, []);

  // Load form data from appStorage (stale drafts are discarded, not restored)
  const loadForm = useCallback((formKey, defaultData = {}) => {
    const storageKey = `${STORAGE_PREFIX}${formKey}`;
    const parsed = readValidDraft(storageKey);
    if (parsed) {
      return { ...defaultData, ...parsed.data };
    }
    return defaultData;
  }, []);

  // Clear form data from appStorage
  const clearForm = useCallback((formKey) => {
    try {
      const storageKey = `${STORAGE_PREFIX}${formKey}`;
      appStorage.removeItem(storageKey);
      setSavedForms(prev => {
        const next = { ...prev };
        delete next[formKey];
        return next;
      });
      // Clear any pending save
      if (saveTimeouts.current[formKey]) {
        clearTimeout(saveTimeouts.current[formKey]);
        delete saveTimeouts.current[formKey];
      }
      console.debug(`AutoSave: Cleared ${formKey}`);
    } catch (e) {
      console.warn('AutoSave: Failed to clear', e);
    }
  }, []);

  // Check if form has saved data (a stale draft counts as none and is purged)
  const hasSavedForm = useCallback((formKey) => {
    const storageKey = `${STORAGE_PREFIX}${formKey}`;
    return readValidDraft(storageKey) !== null;
  }, []);

  // Get save timestamp (stale drafts return null and are purged)
  const getSaveTime = useCallback((formKey) => {
    const storageKey = `${STORAGE_PREFIX}${formKey}`;
    const parsed = readValidDraft(storageKey);
    return parsed && parsed.savedAt ? new Date(parsed.savedAt) : null;
  }, []);

  // Clear all saved forms
  const clearAllForms = useCallback(() => {
    const keysToRemove = [];
    for (let i = 0; i < appStorage.length; i++) {
      const key = appStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => appStorage.removeItem(key));
    setSavedForms({});
  }, []);

  const value = {
    saveForm,
    loadForm,
    clearForm,
    hasSavedForm,
    getSaveTime,
    clearAllForms,
    savedForms
  };

  return (
    <AutoSaveContext.Provider value={value}>
      {children}
    </AutoSaveContext.Provider>
  );
};

/**
 * Hook to access auto-save functionality
 */
export const useAutoSaveContext = () => {
  const context = useContext(AutoSaveContext);
  if (!context) {
    throw new Error('useAutoSaveContext must be used within AutoSaveProvider');
  }
  return context;
};

/**
 * Hook for form-specific auto-save with state management
 * 
 * @param {string} formKey - Unique identifier for the form
 * @param {object} defaultData - Default form values
 * @param {boolean} enabled - Whether auto-save is enabled (default: true)
 */
export const useFormAutoSave = (formKey, defaultData = {}, enabled = true, autoRestore = true) => {
  const { saveForm, loadForm, clearForm, hasSavedForm, getSaveTime } = useAutoSaveContext();
  const [isRestored, setIsRestored] = useState(false);
  
  // Initialize form data from appStorage or default
  const [formData, setFormDataInternal] = useState(() =>
    (enabled && autoRestore) ? loadForm(formKey, defaultData) : defaultData
  );

  // Snapshot the saved draft once at mount, for an explicit restore.
  const initialDraftRef = useRef(enabled ? loadForm(formKey, defaultData) : defaultData);

  // Mark as restored after initial load
  useEffect(() => {
    if (enabled && hasSavedForm(formKey)) {
      setIsRestored(true);
    }
  }, [enabled, formKey, hasSavedForm]);

  // Custom setFormData that also triggers auto-save
  const setFormData = useCallback((updater) => {
    setFormDataInternal((prev) => {
      const newData = typeof updater === 'function' ? updater(prev) : updater;
      
      // Auto-save if enabled
      if (enabled) {
        saveForm(formKey, newData);
      }
      
      return newData;
    });
  }, [enabled, formKey, saveForm]);

  // Clear saved data and reset to default
  const clearSavedData = useCallback(() => {
    clearForm(formKey);
    setFormDataInternal(defaultData);
    setIsRestored(false);
  }, [clearForm, formKey, defaultData]);

  // Explicitly restore the draft snapshotted at mount.
  const restoreDraft = useCallback(() => {
    setFormDataInternal(initialDraftRef.current);
    setIsRestored(true);
  }, []);

  // Check if there's a draft
  const hasDraft = useCallback(() => {
    return enabled && hasSavedForm(formKey);
  }, [enabled, formKey, hasSavedForm]);

  // Get draft timestamp
  const getDraftTime = useCallback(() => {
    return getSaveTime(formKey);
  }, [formKey, getSaveTime]);

  return {
    formData,
    setFormData,
    clearSavedData,
    restoreDraft,
    hasDraft,
    getDraftTime,
    isRestored
  };
};

export default AutoSaveProvider;
